<template>
  <div class="independent-wrapper">
    <div class="content">
      <div class="title">
        <span class="border"></span>
        <i class="el-icon-location-outline"></i> 内容管理 &gt; 关于我们
      </div>
      <div class="contentLeft">
        <el-form
          ref="aboutForm"
          :model="formData"
          :rules="formRules"
          label-width="120px"
          v-loading="loading"
          class="about-form"
        >
          <el-form-item label="品牌 Logo" prop="logoUrl">
            <el-upload
              :show-file-list="false"
              :http-request="onUpload"
              accept=".png,.PNG,.jpg,.JPG,.jpeg,.JPEG,.svg"
            >
              <el-button>上传 Logo</el-button>
            </el-upload>
            <div class="upload-tip">建议 192×192 PNG（透明背景）或 SVG，1:1 正方，≤100KB</div>
            <div v-if="formData.logoUrl" class="logo-preview">
              <img :src="resolveImg(formData.logoUrl)" />
              <span class="del-img" @click="formData.logoUrl = ''">移除</span>
            </div>
          </el-form-item>

          <el-form-item label="品牌名" prop="brandName">
            <el-input v-model="formData.brandName" placeholder="如：健康商城"></el-input>
          </el-form-item>

          <el-form-item label="标语" prop="slogan">
            <el-input v-model="formData.slogan" placeholder="如：舌面智能检测 · 体质辨识 · 中医养护"></el-input>
          </el-form-item>

          <el-form-item label="公司介绍" prop="introText">
            <el-input
              type="textarea"
              :autosize="{ minRows: 4, maxRows: 8 }"
              v-model="formData.introText"
              placeholder="一段话介绍品牌定位、服务理念"
            ></el-input>
          </el-form-item>

          <el-form-item label="服务范围">
            <div class="service-list">
              <div class="service-row" v-for="(item, idx) in formData.serviceItemsArr" :key="idx">
                <el-upload
                  class="svc-upload"
                  :show-file-list="false"
                  :http-request="(o) => onUploadServiceIcon(o, idx)"
                  accept=".png,.PNG,.jpg,.JPG,.jpeg,.JPEG,.svg"
                >
                  <div class="svc-icon">
                    <img v-if="item.icon" :src="resolveImg(item.icon)" />
                    <span v-else class="svc-icon-empty">+</span>
                  </div>
                </el-upload>
                <el-input v-model="item.title" placeholder="服务标题" class="svc-title"></el-input>
                <el-input v-model="item.desc" placeholder="服务说明" class="svc-desc"></el-input>
                <el-button type="danger" plain size="small" @click="removeServiceItem(idx)">删除</el-button>
              </div>
              <el-button class="add-svc" @click="addServiceItem">添加一项</el-button>
            </div>
            <div class="upload-tip">服务图标建议 96×96 PNG（透明背景）或 SVG，1:1 正方，≤50KB；图标可留空，将自动渲染圆点</div>
          </el-form-item>

          <el-form-item label="品牌历史" prop="historyText">
            <el-input
              type="textarea"
              :autosize="{ minRows: 6, maxRows: 12 }"
              v-model="formData.historyText"
              placeholder="品牌历史渊源、企业故事"
            ></el-input>
          </el-form-item>

          <el-form-item label="版本号" prop="version">
            <el-input v-model="formData.version" style="width: 200px" placeholder="如：1.0.0"></el-input>
          </el-form-item>

          <el-form-item label="用户协议" prop="userAgreement">
            <el-input
              type="textarea"
              :autosize="{ minRows: 8, maxRows: 20 }"
              v-model="formData.userAgreement"
              placeholder="登录页《用户协议》正文，每行一条，换行处即换行显示"
            ></el-input>
            <div class="upload-tip">用于患者端登录页《用户协议》弹框。直接换行分条，无需 HTML。</div>
          </el-form-item>

          <el-form-item label="隐私条款" prop="privacyPolicy">
            <el-input
              type="textarea"
              :autosize="{ minRows: 8, maxRows: 20 }"
              v-model="formData.privacyPolicy"
              placeholder="登录页《隐私条款》正文，每行一条，换行处即换行显示"
            ></el-input>
            <div class="upload-tip">用于患者端登录页《隐私条款》弹框。直接换行分条，无需 HTML。</div>
          </el-form-item>

          <el-form-item>
            <el-button type="primary" class="addBtn" @click="submit" :loading="submitting">保存</el-button>
            <el-button @click="fetchDetail">重新加载</el-button>
          </el-form-item>
        </el-form>
      </div>
    </div>
  </div>
</template>

<script>
import * as cmsApi from '../../api/cms'
import { SPECS, checkSpec } from '@/utils/imageSpec'

function emptyForm() {
  return {
    id: 1,
    brandName: '',
    slogan: '',
    logoUrl: '',
    introText: '',
    serviceItems: '',
    serviceItemsArr: [],
    historyText: '',
    version: '',
    userAgreement: '',
    privacyPolicy: '',
  }
}

function parseServiceItems(raw) {
  if (!raw) return []
  if (Array.isArray(raw)) return raw.map(normalizeItem)
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    if (Array.isArray(parsed)) return parsed.map(normalizeItem)
  } catch (e) {
    // 旧数据为纯字符串列表，单元素降级
    return [{ icon: '', title: String(raw), desc: '' }]
  }
  return []
}

function normalizeItem(it) {
  if (typeof it === 'string') return { icon: '', title: it, desc: '' }
  return {
    icon: it.icon || it.icon_url || '',
    title: it.title || '',
    desc: it.desc || '',
  }
}

export default {
  data() {
    return {
      loading: false,
      submitting: false,
      formData: emptyForm(),
      formRules: {
        brandName: [{ required: true, message: '请输入品牌名', trigger: 'blur' }],
        slogan: [{ required: true, message: '请输入标语', trigger: 'blur' }],
        introText: [{ required: true, message: '请输入公司介绍', trigger: 'blur' }],
      },
    }
  },
  mounted() {
    this.fetchDetail()
  },
  methods: {
    resolveImg(url) {
      if (!url) return ''
      if (/^https?:\/\//.test(url)) return url
      if (url.startsWith('/')) return cmsApi.imageBaseUrl.replace(/\/$/, '') + url
      return cmsApi.imageBaseUrl + url
    },
    async fetchDetail() {
      this.loading = true
      try {
        const res = await cmsApi.aboutGet()
        if (res.data && res.data.code === 0) {
          const d = res.data.data || {}
          this.formData = {
            ...emptyForm(),
            ...d,
            serviceItemsArr: parseServiceItems(d.serviceItems),
          }
        } else {
          this.$message.error(res.data?.msg || '加载失败')
        }
      } catch (e) {
        this.$message.error('网络异常')
      } finally {
        this.loading = false
      }
    },
    addServiceItem() {
      this.formData.serviceItemsArr.push({ icon: '', title: '', desc: '' })
    },
    removeServiceItem(idx) {
      this.formData.serviceItemsArr.splice(idx, 1)
    },
    async onUpload(option) {
      const file = option.file
      if (!file) return
      if (!(await checkSpec(this, file, SPECS.ABOUT_LOGO))) return
      try {
        const res = await cmsApi.uploadImage(file)
        const path = res.data?.data || res.data?.msg
        if (res.data && res.data.code === 0 && path) {
          this.formData.logoUrl = path
          this.$message.success('上传成功')
        } else {
          this.$message.error(res.data?.msg || '上传失败')
        }
      } catch (e) {
        this.$message.error('上传失败')
      }
    },
    async onUploadServiceIcon(option, idx) {
      const file = option.file
      if (!file) return
      if (!(await checkSpec(this, file, SPECS.ABOUT_SVC_ICON))) return
      try {
        const res = await cmsApi.uploadImage(file)
        const path = res.data?.data || res.data?.msg
        if (res.data && res.data.code === 0 && path) {
          this.formData.serviceItemsArr[idx].icon = path
        } else {
          this.$message.error(res.data?.msg || '上传失败')
        }
      } catch (e) {
        this.$message.error('上传失败')
      }
    },
    submit() {
      this.$refs.aboutForm.validate(async (valid) => {
        if (!valid) return
        this.submitting = true
        try {
          const payload = {
            id: 1,
            brandName: this.formData.brandName,
            slogan: this.formData.slogan,
            logoUrl: this.formData.logoUrl,
            introText: this.formData.introText,
            serviceItems: JSON.stringify(this.formData.serviceItemsArr || []),
            historyText: this.formData.historyText,
            version: this.formData.version,
            userAgreement: this.formData.userAgreement,
            privacyPolicy: this.formData.privacyPolicy,
          }
          const res = await cmsApi.aboutEdit(payload)
          if (res.data && res.data.code === 0) {
            this.$message.success('保存成功')
            this.fetchDetail()
          } else {
            this.$message.error(res.data?.msg || '保存失败')
          }
        } catch (e) {
          this.$message.error('网络异常')
        } finally {
          this.submitting = false
        }
      })
    },
  },
}
</script>

<style lang="less" scoped>
@import "../../../static/css/layout.less";

.independent-wrapper {
  width: 100%;
  height: 100%;
  overflow-y: auto;
}
.content {
  padding: 20px;
  .title {
    font-size: 14px;
    color: #333;
    margin-bottom: 20px;
    .border {
      display: inline-block;
      width: 3px;
      height: 14px;
      background: #679ff6;
      margin-right: 8px;
      vertical-align: middle;
    }
  }
}
.about-form {
  max-width: 880px;
}
.addBtn {
  background: #679ff6;
  border-color: #679ff6;
  color: #fff;
}
.logo-preview {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  img {
    max-width: 140px;
    max-height: 80px;
    border: 1px solid #eee;
    border-radius: 4px;
  }
  .del-img {
    color: #f56c6c;
    cursor: pointer;
    font-size: 12px;
  }
}
.upload-tip {
  color: #909399;
  font-size: 12px;
  margin-top: 4px;
  line-height: 1.5;
}
.service-list {
  width: 100%;
  .service-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    .svc-upload {
      flex: 0 0 56px;
    }
    .svc-icon {
      width: 56px;
      height: 56px;
      border: 1px dashed #ccc;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      overflow: hidden;
      img {
        max-width: 100%;
        max-height: 100%;
      }
      .svc-icon-empty {
        color: #999;
        font-size: 22px;
      }
    }
    .svc-title {
      flex: 0 0 200px;
    }
    .svc-desc {
      flex: 1 1 auto;
    }
  }
  .add-svc {
    margin-top: 4px;
  }
}
</style>
